/**
 * OPO — Cornelius token & memory usage (Tier-3 D2).
 * Sources /api/opo/cornelius-memories/usage-stats.
 */
import React, { useState, useEffect, useCallback } from 'react';

const card = {
  background: 'var(--card-bg)',
  border: '1px solid var(--border-color)',
  borderRadius: 'var(--border-radius)',
  padding: '14px 16px',
  marginBottom: 14,
  color: 'var(--text-primary)',
};

const inputStyle = {
  padding: '7px 12px',
  borderRadius: 'var(--border-radius)',
  border: '1px solid var(--border-color)',
  background: 'var(--surface-color)',
  color: 'var(--text-primary)',
  fontSize: 13,
};

function btn(variant) {
  const map = {
    primary: { background: 'var(--primary-color)', color: 'var(--text-on-primary)' },
  };
  return {
    padding: '7px 16px',
    borderRadius: 'var(--border-radius)',
    border: 'none',
    fontSize: 13,
    cursor: 'pointer',
    fontWeight: 600,
    ...map[variant || 'primary'],
  };
}

function Sparkline({ values, label, width = 320, height = 60, stroke = 'var(--primary-color)' }) {
  if (!values || values.length === 0) {
    return <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>No data for {label}.</div>;
  }
  const nums = values.map(v => Number(v) || 0);
  const max = Math.max(1, ...nums);
  const min = Math.min(0, ...nums);
  const range = max - min || 1;
  const step = nums.length > 1 ? width / (nums.length - 1) : width;
  const points = nums
    .map((v, i) => `${(i * step).toFixed(1)},${(height - ((v - min) / range) * (height - 8) - 4).toFixed(1)}`)
    .join(' ');
  return (
    <div>
      <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>
        {label} (max {max})
      </div>
      <svg width={width} height={height} style={{ display: 'block' }}>
        <polyline fill="none" stroke={stroke} strokeWidth={2} points={points} />
      </svg>
    </div>
  );
}

export default function AdminCorneliusUsage() {
  const [userId, setUserId] = useState('13');
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!userId.trim()) return;
    setLoading(true);
    setError('');
    try {
      const r = await fetch(`/api/opo/cornelius-memories/usage-stats?user_id=${encodeURIComponent(userId.trim())}&days=${days}`);
      const d = await r.json();
      if (!r.ok || d.success === false) throw new Error(d.error || `HTTP ${r.status}`);
      setData(d);
    } catch (e) {
      setError(e.message);
      setData(null);
    }
    setLoading(false);
  }, [userId, days]);

  useEffect(() => { load(); }, [load]);

  const promptSeries = (data?.token_usage || []).map(r => r.prompt_tokens);
  const outputSeries = (data?.token_usage || []).map(r => r.output_tokens);
  const callsSeries = (data?.token_usage || []).map(r => r.llm_calls);
  const memSeries = (data?.memory_growth || []).map(r => r.rows_added);

  return (
    <div style={{ maxWidth: 1000 }}>
      <div style={{ marginBottom: 16, color: 'var(--text-secondary)', fontSize: 13 }}>
        Tier-3 token and memory usage — pulled from <code>gfa_token_usage</code> + <code>assistant_memory</code>.
      </div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 18, flexWrap: 'wrap' }}>
        <input value={userId} onChange={e => setUserId(e.target.value)} placeholder="User ID" style={{ ...inputStyle, width: 160 }} />
        <select value={days} onChange={e => setDays(parseInt(e.target.value, 10))} style={{ ...inputStyle, cursor: 'pointer' }}>
          {[7, 14, 30, 60, 90, 180].map(d => <option key={d} value={d}>{d} days</option>)}
        </select>
        <button onClick={load} style={btn('primary')}>Refresh</button>
      </div>

      {error && <div style={{ color: 'var(--danger-color)', marginBottom: 12, fontSize: 13 }}>{error}</div>}
      {loading && <div style={{ color: 'var(--text-secondary)' }}>Loading…</div>}

      {data && (
        <>
          <div style={card}>
            <strong>Memory totals</strong>
            <div style={{ display: 'flex', gap: 28, marginTop: 8, fontSize: 13, color: 'var(--text-secondary)' }}>
              <div>Total rows: <strong style={{ color: 'var(--text-primary)' }}>{data.memory_totals?.rows_total ?? 0}</strong></div>
              <div>Quarantined: <strong style={{ color: 'var(--text-primary)' }}>{data.memory_totals?.rows_quarantined ?? 0}</strong></div>
              <div>Consolidated: <strong style={{ color: 'var(--text-primary)' }}>{data.memory_totals?.rows_consolidated ?? 0}</strong></div>
            </div>
          </div>

          <div style={card}>
            <strong>Memory growth (rows/day)</strong>
            <div style={{ marginTop: 8 }}>
              <Sparkline values={memSeries} label={`memory rows added over ${days}d`} stroke="var(--success-color)" />
            </div>
          </div>

          <div style={card}>
            <strong>LLM token usage</strong>
            <div style={{ display: 'grid', gap: 14, gridTemplateColumns: '1fr 1fr', marginTop: 8 }}>
              <Sparkline values={promptSeries} label="prompt tokens/day" />
              <Sparkline values={outputSeries} label="output tokens/day" stroke="darkorange" />
              <Sparkline values={callsSeries} label="LLM calls/day" stroke="var(--text-primary)" />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
